import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  TextField, 
  Button, 
  Grid, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Select, 
  Divider,
  FormHelperText,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import { useForm, Controller, useWatch, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import PageContainer from "../../components/layout/PageContainer";
import { StockTransactionType } from '../../types/inventory';
import { Item } from '../../types/item';
import { Location } from '../../types/location';

// Mock data - replace with API calls
const mockItems: Item[] = [
  {
    id: '1',
    itemCode: 'ITEM-001',
    itemName: 'Paracetamol',
    description: 'Pain reliever',
    price: 5.99,
    status: 'active',
    itemType: 'inventory',
    itemGroupId: 'group-1',
    unitOfMeasureId: 'uom-1',
    reorderLevel: 50,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: '2',
    itemCode: 'ITEM-002',
    itemName: 'Ibuprofen',
    description: 'Anti-inflammatory',
    price: 8.99,
    status: 'active',
    itemType: 'inventory',
    itemGroupId: 'group-1',
    unitOfMeasureId: 'uom-1',
    reorderLevel: 30,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
];

const mockLocations: Location[] = [
  {
    id: '1',
    name: 'Main Pharmacy',
    code: 'PH-001',
    locationType: 'Pharmacy',
    status: true,
    locationSubType: 'Main',
    facilityId: 'facility-1',
    facilityName: 'Main Facility',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: '2',
    name: 'Emergency Ward',
    code: 'WARD-ER',
    locationType: 'Ward',
    status: true,
    locationSubType: 'Emergency',
    facilityId: 'facility-1',
    facilityName: 'Main Facility',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: '3',
    name: 'OPD',
    code: 'WARD-OPD',
    locationType: 'Ward',
    status: true,
    locationSubType: 'Outpatient',
    facilityId: 'facility-1',
    facilityName: 'Main Facility',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
];

// Schema for individual item in the form
const itemSchema = yup.object().shape({
  itemId: yup.string().required('Item is required'),
  quantity: yup
    .number()
    .typeError('Quantity must be a number')
    .required('Quantity is required')
    .min(1, 'Quantity must be at least 1'),
  batchNumber: yup.string(),
});

// Main form schema
const schema = yup.object({
  itemId: yup.string().required('Item is required'),
  fromLocationId: yup.string().required('From Location is required'),
  quantity: yup
    .number()
    .typeError('Quantity must be a number')
    .required('Quantity is required')
    .min(1, 'Quantity must be at least 1'),
  transactionType: yup
    .mixed<StockTransactionType.DISPENSED | StockTransactionType.DISPOSED | StockTransactionType.TRANSFER_OUT>()
    .oneOf([
      StockTransactionType.DISPENSED,
      StockTransactionType.DISPOSED,
      StockTransactionType.TRANSFER_OUT,
    ], 'Invalid transaction type')
    .required('Transaction Type is required'),
  toLocationId: yup.string()
    .when('transactionType', {
      is: StockTransactionType.TRANSFER_OUT,
      then: (schema) => schema.required('To Location is required for transfer'),
    })
    .optional(),
  reference: yup.string().optional(),
  notes: yup.string().optional(),
  batchNumber: yup.string().optional(),
  expiryDate: yup.string().optional(),
}).required();

const StockOutPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  type FormData = {
    itemId: string;
    fromLocationId: string;
    quantity: number;
    transactionType: StockTransactionType.DISPENSED | StockTransactionType.DISPOSED | StockTransactionType.TRANSFER_OUT;
    toLocationId?: string;
    reference?: string;
    notes?: string;
    batchNumber?: string;
    expiryDate?: string;
  };

  const { control, handleSubmit, formState: { errors }, reset } = useForm<FormData>({
    resolver: yupResolver(schema) as any, // Type assertion to handle yup and react-hook-form types
    defaultValues: {
      itemId: '',
      fromLocationId: '',
      quantity: 1,
      transactionType: StockTransactionType.DISPENSED,
      toLocationId: undefined,
      reference: '',
      notes: '',
    },
  });

  const transactionType = useWatch({
    control,
    name: 'transactionType',
    defaultValue: StockTransactionType.DISPENSED
  });

  const isTransfer = transactionType === StockTransactionType.TRANSFER_OUT;
  const fromLocationId = useWatch({ control, name: 'fromLocationId', defaultValue: '' });
  const toLocationId = useWatch({ control, name: 'toLocationId', defaultValue: '' });
  const reference = useWatch({ control, name: 'reference', defaultValue: '' });
  const notes = useWatch({ control, name: 'notes', defaultValue: '' });

  const onSubmit: SubmitHandler<FormData> = async (data) => {
    try {
      setIsSubmitting(true);
      // TODO: Replace with actual API call
      console.log('Submitting stock out:', data);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      enqueueSnackbar('Stock out recorded successfully', { variant: 'success' });
      reset({
        itemId: '',
        fromLocationId: '',
        quantity: 1,
        transactionType: StockTransactionType.DISPENSED,
        toLocationId: undefined,
        reference: '',
        notes: '',
      });
    } catch (error) {
      console.error('Error recording stock out:', error);
      enqueueSnackbar('Failed to record stock out', { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getTransactionTypeLabel = (type: StockTransactionType) => {
    switch (type) {
      case StockTransactionType.DISPENSED:
        return 'Dispensed to Patient';
      case StockTransactionType.DISPOSED:
        return 'Disposed';
      case StockTransactionType.TRANSFER_OUT:
        return 'Transfer to Another Location';
      default:
        return type;
    }
  };

  // Helper function to find item by ID
  const findItemById = (id: string) => {
    return mockItems.find((item) => item.id === id) || null;
  };

  return (
    <PageContainer title="Stock Out">
      <Paper sx={{ p: 3, mt: 2 }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Controller
                name="fromLocationId"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.fromLocationId}>
                    <InputLabel>From Location *</InputLabel>
                    <Select
                      {...field}
                      label="From Location *"
                      error={!!errors.fromLocationId}
                    >
                      {mockLocations.map((location) => (
                        <MenuItem key={location.id} value={location.id}>
                          {location.name} ({location.code})
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.fromLocationId && (
                      <FormHelperText>{errors.fromLocationId.message}</FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Controller
                name="transactionType"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.transactionType}>
                    <InputLabel>Transaction Type *</InputLabel>
                    <Select
                      {...field}
                      label="Transaction Type *"
                      error={!!errors.transactionType}
                    >
                      <MenuItem value={StockTransactionType.DISPENSED}>
                        {getTransactionTypeLabel(StockTransactionType.DISPENSED)}
                      </MenuItem>
                      <MenuItem value={StockTransactionType.DISPOSED}>
                        {getTransactionTypeLabel(StockTransactionType.DISPOSED)}
                      </MenuItem>
                      <MenuItem value={StockTransactionType.TRANSFER_OUT}>
                        {getTransactionTypeLabel(StockTransactionType.TRANSFER_OUT)}
                      </MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
            
            {transactionType === StockTransactionType.TRANSFER_OUT && (
              <Grid item xs={12} md={6}>
                <Controller
                  name="toLocationId"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.toLocationId}>
                      <InputLabel>To Location *</InputLabel>
                      <Select
                        {...field}
                        label="To Location *"
                        error={!!errors.toLocationId}
                      >
                        {mockLocations
                          .filter(loc => loc.id !== fromLocationId)
                          .map((location) => (
                            <MenuItem key={location.id} value={location.id}>
                              {location.name} ({location.code})
                            </MenuItem>
                          ))}
                      </Select>
                      {errors.toLocationId && (
                        <FormHelperText>{errors.toLocationId.message}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
              </Grid>
            )}
            
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Items</Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    // Handle adding an item
                  }}
                  disabled={!fromLocationId}
                >
                  Add Item
                </Button>
              </Box>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={5}>
                  <Controller
                    name="itemId"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        select
                        label="Item *"
                        fullWidth
                        error={!!errors.itemId}
                        helperText={errors.itemId?.message}
                      >
                        {mockItems.map((item) => (
                          <MenuItem key={item.id} value={item.id}>
                            {`${item.itemCode} - ${item.itemName}`}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Grid>
                
                <Grid item xs={12} md={2}>
                  <Controller
                    name="quantity"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        type="number"
                        label="Quantity *"
                        fullWidth
                        inputProps={{ min: 1 }}
                        error={!!errors.quantity}
                        helperText={errors.quantity?.message}
                        onChange={(e) => field.onChange(parseInt(e.target.value) || 1)}
                      />
                    )}
                  />
                </Grid>
                  
                  <Grid item xs={12} md={isTransfer ? 2 : 3}>
                  <Controller
                    name="batchNumber"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Batch #"
                        fullWidth
                      />
                    )}
                  />
                </Grid>
                  
                  {isTransfer && (
                    <Grid item xs={12} md={2}>
                      <Controller
                        name="expiryDate"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            type="date"
                            label="Expiry Date"
                            InputLabelProps={{
                              shrink: true,
                            }}
                            fullWidth
                          />
                        )}
                      />
                    </Grid>
                  )}
                  
                </Grid>
              </Grid>
              
              <Grid item xs={12}>
                <Controller
                  name="reference"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Reference #"
                      fullWidth
                      margin="normal"
                      helperText="e.g., Prescription #, Disposal reference, etc."
                    />
                  )}
                />
                
                <Controller
                  name="notes"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Notes"
                      fullWidth
                      multiline
                      rows={3}
                      margin="normal"
                    />
                  )}
                />
              </Grid>
              
              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                <Button 
                  variant="outlined" 
                  onClick={() => reset()}
                  disabled={isSubmitting}
                >
                  Reset
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Processing...' : 'Record Stock Out'}
                </Button>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </PageContainer>
  );
};

export default StockOutPage;
